import { ThemeProvider, StyledEngineProvider } from '@mui/material/styles';
import React, { type FunctionComponent, useCallback, useEffect, createRef } from 'react';
import { BrowserRouter, Route, Routes } from 'react-router-dom';
import { type SnackbarKey, SnackbarProvider } from 'notistack';
import { Button } from '@mui/material';
import { setupi18n, TranslationProvider, useTranslation } from 'common';
import { QueryClient, QueryClientProvider } from 'react-query';
import { ApplicationInsightsProvider, useApplicationInsights } from './ApplicationInsights';
import { AppPreload } from './AppPreload';
import { userManager, withOidc } from './auth';
import Modal from './components/Modal/Modal';
import { ModalProvider } from './contexts/ModalProvider/ModalProvider';
import ErrorBoundary from './ErrorBoundary';
import ContentSwitch from './layout/ContentSwitch/ContentSwitch';
import Navbar from './layout/Navbar/Navbar';
import NewMembershipPage from './pages/NewMembershipPage/NewMembershipPage';
import Routing from './routing/routing';
import Theme from './theming/theme';
import { useScreenSizeProvider } from './hooks/useScreenSizeProvider';
import { MobileHomeComponent } from './layout/Navbar/MobileDeviceNavbar/MobileDeviceNavbar';
import MailValidationPage from './pages/NewMembershipPage/newMemberFlow/flowPages/MailValidationPage/MailValidationPage';
import { MessageProvider } from './contexts/MessageContext';
import { UserSelectionProvider } from './contexts/UserSelectionContext';
import { FullScreenLoadingComponent } from './components/misc';
import FslAutoRedirectPage from './pages/FslAutoRedirectPage/FslAutoRedirectPage';
import FslCallbackPage from './pages/FslCallback/FslCallbackPage';

// declare module '@mui/styles/defaultTheme' {
//   // eslint-disable-next-line @typescript-eslint/no-empty-interface
//   interface DefaultTheme extends Theme {}
// }

const queryClient = new QueryClient();

const danishTranslation = require('./translations/danish.json');
const englishTranslation = require('./translations/english.json');

setupi18n([danishTranslation, englishTranslation]);

const MemberFlow: FunctionComponent = () => {
  const { screenSize } = useScreenSizeProvider();
  const smallScreen = screenSize === 'SmallScreen';
  return (
    <UserSelectionProvider>
      <MessageProvider>
        <div style={{ display: 'flex' }}>
          <Navbar smallScreen={smallScreen} />
          {!smallScreen && <ContentSwitch homeComponent={MobileHomeComponent} />}
        </div>
      </MessageProvider>
    </UserSelectionProvider>
  );
};

const MemberFlowWithOidc = withOidc(MemberFlow);

const PerformLogout: FunctionComponent = () => {
  useEffect(() => {
    userManager.signoutRedirect();
  }, []);

  return <FullScreenLoadingComponent />;
};

function PortalRouter() {
  return (
    <Routes>
      <Route path={Routing.newMember} element={<NewMembershipPage />} />
      <Route path={Routing.logout} element={<PerformLogout />} />
      <Route
        path={`${Routing.mailValidation}/:id`}
        element={
          <NewMembershipPage>
            <MailValidationPage />
          </NewMembershipPage>
        }
      />

      {/* The following routes are so far only for debugging.
  If enabled in production you need to make sure that state is stored in something else than memeory, perhaps local or session storage */}

      {/* <Route path="/nytInformation" component={() => <NewMembershipPage step={1} />} exact />
      {/* <Route path="/nytInformation" element={<NewMembershipPage step={1} />} exact />
  <Route path="/nytPosition" element={<NewMembershipPage step={2} />} exact />
  <Route path="/godkend" element={<NewMembershipPage step={3} />} exact />
  <Route path="/tak" element={<NewMembershipPage step={4} />} exact /> */}
      <Route path={Routing.fslAutoRedirectLogin} element={<FslAutoRedirectPage />} />
      <Route path="/callback" element={<FslCallbackPage />} />
      <Route path={Routing.fslCallback} element={<FslCallbackPage />} />
      <Route path="*" element={<MemberFlowWithOidc />} />
    </Routes>
  );
}

function App() {
  const notistackRef = createRef<SnackbarProvider>();
  const onClickDismiss = useCallback(
    (key: SnackbarKey) => () => {
      notistackRef.current?.closeSnackbar(key);
    },
    [notistackRef]
  );

  return (
    <BrowserRouter
      future={{
        v7_startTransition: true,
      }}
    >
      <ApplicationInsightsProvider>
        <StyledEngineProvider injectFirst>
          <QueryClientProvider client={queryClient}>
            <ThemeProvider theme={Theme}>
              <TranslationProvider>
                <SnackbarProvider ref={notistackRef} maxSnack={3} action={(key) => <Button onClick={onClickDismiss(key)}>Luk</Button>}>
                  <UseErrorBoundary>
                    <ModalProvider>
                      <Modal />
                      <AppPreload App={PortalRouter} />
                    </ModalProvider>
                  </UseErrorBoundary>
                </SnackbarProvider>
              </TranslationProvider>
            </ThemeProvider>
          </QueryClientProvider>
        </StyledEngineProvider>
      </ApplicationInsightsProvider>
    </BrowserRouter>
  );
}

const UseErrorBoundary: FunctionComponent<{ children: JSX.Element }> = ({ children }) => {
  const { trackException } = useApplicationInsights();
  const { translateString } = useTranslation();
  return (
    <ErrorBoundary
      primaryMessage={translateString('general_primary_error_message')}
      secondaryMesage={translateString('general_secondary_error_message')}
      logException={trackException}
    >
      {children}
    </ErrorBoundary>
  );
};

export default App;
